<script lang="ts">
  import { untrack } from "svelte";
  import { formatAge, type StrikeExpiryTypedResponse, type StrikeExpirySum7dResponse } from "./api";
  import { GEX_METRIC_LABELS, gexGridModel, sum7dModel, type GexMetric, type GridCell, type GridColumn } from "./gexGrid";
  import { ageVerdict, spotHead, spotProvenanceNote, gex2SpotRule, type Gex2Feed } from "./gex2";
  import { LABEL_FONT_MICRO } from "./layout";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  interface Props {
    gridLoaded?: boolean;
    feedLoaded?: boolean;
    venueClosed?: boolean;
    gridAgeBody: StrikeExpiryTypedResponse | null;
    gridBody: StrikeExpiryTypedResponse | null;
    gridFetchedAtMs: number | null;
    gridStatus: number;
    gridError: string | null;
    sumLoaded?: boolean;
    sumAgeBody: StrikeExpirySum7dResponse | null;
    sumBody: StrikeExpirySum7dResponse | null;
    sumFetchedAtMs: number | null;
    sumStatus: number;
    sumError: string | null;
    feed: Gex2Feed | null;
    microFontSvg: number;
    budgetS: number;
    spotError: string | null;
    preferenceError: string | null;
  }
  let { gridLoaded = false, feedLoaded = false, venueClosed = false, gridAgeBody, gridBody, gridFetchedAtMs, gridStatus, gridError, sumLoaded = false, sumAgeBody, sumBody, sumFetchedAtMs, sumStatus, sumError, feed, microFontSvg, budgetS, spotError, preferenceError }: Props = $props();
  const instanceId = $props.id();
  const hatchId = `${instanceId}-gex-grid-hatch`;
  let bodyEl = $state<HTMLDivElement | null>(null);
  let manualScroll = $state(false);
  // The scroller's first frame has no height; centring waits for one, then lastCentered keeps resizes from re-centring.
  let viewportHeight = $state(0);
  let lastCentered: string | null = null;
  let lastWindowLog: string | null = null;
  let gridMetric = $state<GexMetric>("gex");
  let metricLabel = $derived(GEX_METRIC_LABELS.find(([metric]) => metric === gridMetric)?.[1] ?? "UNKNOWN");
  let gridInfoOpen = $state(false);
  let gridEl = $state<HTMLDivElement | null>(null);
  let viewportWidth = $state(0);
  let gridReadout = $state<string | null>(null);
  let windowSpot = $derived(spotHead(feed));
  let windowAge = $derived(ageVerdict(feed, budgetS));
  let windowAgeLabel = $derived(windowAge?.kind === "live" || windowAge?.kind === "stale"
    ? `SNAPSHOT ${windowAge.kind === "stale" ? "STALE " : "AGE "}${windowAge.ageS}s`
    : "SNAPSHOT AGE UNSERVED");
  let windowAlert = $derived(venueClosed || (feed === null && !feedLoaded) ? null : windowSpot.kind === "unserved"
    ? `WINDOW SPOT ABSENT · ${windowAgeLabel}`
    : windowAge?.kind !== "live" || spotError !== null ? `WINDOW SPOT ${windowSpot.text} · ${windowAgeLabel}` : null);
  $effect(() => {
    const state = windowAlert === null ? null : `${windowSpot.kind}:${windowAge?.kind ?? "unserved"}:${spotError ?? ""}`;
    if (bodyEl?.closest(".cockpit-hidden, [hidden]")) return;
    if (state !== lastWindowLog && state !== null) console.warn(`[GEX GRID] ${windowAlert}`);
    lastWindowLog = state;
  });
  let grid = $derived.by(() => {
    const parsed = gexGridModel(gridBody, gridMetric, gridStatus);
    if (!parsed.ok || !gridBody?.ok || windowSpot.kind !== "served") return parsed;
    // The ladder door's existing ±200 point window, centred on the same served spot.
    return gexGridModel({ ...gridBody, rows: gridBody.rows.filter(r => Math.abs(r.strike - (windowSpot.kind === "served" ? windowSpot.spot : 0)) <= 200) }, gridMetric, gridStatus);
  });
  let gridRows = $derived(grid.ok ? [...grid.strikes].reverse() : []);
  const W = 320, ROW_H = 13, PAD_B = 10, GUTTER = 46, GRID_TOP = 8, HEADER_H = 42;
  let gridHeight = $derived(GRID_TOP + Math.max(1, gridRows.length) * ROW_H + PAD_B);
  let renderedScale = $derived(viewportWidth / W);
  let labelFontSvg = $derived(Math.min(microFontSvg, LABEL_FONT_MICRO / (renderedScale || 1)));
  // ORDER_HUD_UW_FACE_1_BOX2 Part C: the 7-day sum is the rightmost column, set apart, on its own scale.
  const SUM_GAP = 6;
  let sum = $derived(!sumLoaded ? { ok: false as const, refusal: "SUM LOADING" }
    : sumBody === null && sumError !== null ? { ok: false as const, refusal: sumError } : sum7dModel(sumBody, gridMetric, sumStatus));
  let sumCells = $derived(new Map(sum.ok ? sum.cells.map(c => [c.strike, c] as const) : []));
  let columnWidth = $derived(grid.ok ? (W - GUTTER - SUM_GAP) / (grid.columns.length + 1) : 0);
  let sumX = $derived(grid.ok ? GUTTER + grid.columns.length * columnWidth + SUM_GAP : 0);
  // A refusal name runs to 15 characters; as the grid's refused columns do, the header says REFUSED and the titles name it.
  // The sum is fetched apart, so it wears its own STALE (codex review r1 P2 1), never the grid's chip; its last-session
  // boards at a CLOSED venue say CLOSED (ORDER_HUD_GEX_GRID_CLOSED_1 R4).
  let sumHeaderLines = $derived(["Σ7D", sum.ok ? sum.header : "REFUSED", ...(sum.ok && sum.freshness !== "CHECKED" ? [sum.freshness] : [])]);
  let sumHeaderFont = $derived(Math.min(labelFontSvg, (columnWidth - 4) / (Math.max(...sumHeaderLines.map(line => line.length)) * 0.75)));
  let gridSpot = $derived(gex2SpotRule(feed, gridRows, { padT: GRID_TOP, rowH: ROW_H }).placement);
  let gridCells = $derived(new Map(grid.ok ? grid.cells.map(c => [`${c.expiry}:${c.strike}`, c] as const) : []));
  let gridRefusal = $derived(!gridLoaded ? "GRID LOADING" : gridBody === null && gridError !== null ? gridError : (grid.ok ? null : grid.refusal));
  function readGridCell(column: GridColumn, cell?: GridCell) {
    gridReadout = `${column.expiry} · ${cell?.strike ?? "column"} · ${cell?.absent ? "ABSENT" : cell?.value === undefined ? "NO CELLS" : String(cell.value)} · BOARD ${column.boardDate ?? "UNKNOWN"} · ${column.state} · checked ${column.checkedTs ?? "UNKNOWN"}`;
  }
  let centreKey = $derived(JSON.stringify([
    windowSpot.kind === "served" ? windowSpot.spot : gridMetric,
    grid.ok ? [grid.boardPill, grid.columns.map(c => [c.expiry, c.boardDate, c.state]), gridBody?.ok ? gridBody.rows.map(r => [r.expiry, r.strike, r.net_gex, r.net_delta, r.net_charm, r.net_vanna]) : []] : grid.refusal,
  ]));
  $effect(() => {
    const key = centreKey;
    const el = gridEl;
    const scale = renderedScale;
    const sized = viewportHeight > 0;
    if (!el || !sized || scale <= 0 || manualScroll || key === lastCentered) return;
    untrack(() => {
      if (el.clientHeight <= 0) return;
      let centreY = gridSpot.kind !== "absent" ? gridSpot.y : GRID_TOP + Math.floor(gridRows.length / 2) * ROW_H;
      if (gridSpot.kind === "absent" && grid.ok) {
        const peak = grid.cells.reduce<GridCell | null>((best, cell) =>
          cell.value !== null && (best === null || Math.abs(cell.value) > Math.abs(best.value ?? 0)) ? cell : best, null);
        if (peak) centreY = GRID_TOP + gridRows.indexOf(peak.strike) * ROW_H + ROW_H / 2;
      }
      el.scrollTop = Math.max(0, centreY * scale - el.clientHeight / 2);
      lastCentered = key;
    });
  });
  function headerLines(column: GridColumn): string[] {
    const lines = column.hatched
      ? column.state === "INCOMPLETE" ? ["INCOM", "PLETE"] : ["CAPTURE", "REFUSED"]
      : [column.expiry.slice(5), ...(columnWidth * renderedScale >= 48 ? [column.header.split(" ")[1]] : [])];
    if (grid.ok && grid.boardPill.startsWith("BOARD MIXED")) lines.push(column.boardDate?.slice(5) ?? "UNKNOWN");
    return lines;
  }
  function headerFont(column: GridColumn): number {
    return Math.min(labelFontSvg, (columnWidth - 4) / (Math.max(...headerLines(column).map(line => line.length)) * 0.75));
  }
  $effect(() => { gridMetric; gridBody; gridReadout = null; });
  // R5: a day-old board at a CLOSED venue is a capture that missed its session; said once per board, never silent.
  let staleAtClosed = $derived(grid.ok && grid.freshness === "STALE" && gridBody?.ok && gridBody.venue_state?.verdict === "CLOSED"
    ? `board ${gridBody.board_date ?? "MIXED"} · last session open ${gridBody.venue_state.last_session_open ?? "UNSERVED"}` : null);
  let staleAtClosedLogged: string | null = null;
  $effect(() => {
    if (staleAtClosed !== null && staleAtClosed !== staleAtClosedLogged) console.error(`[GexGridView] STALE AT CLOSED · ${staleAtClosed}`);
    staleAtClosedLogged = staleAtClosed;
  });

</script>

<div class="grid-body" bind:this={bodyEl}>
  <header class="panel-id grid-head">
    <h3 class="railsec">GEX GRID</h3>
    <div class="grid-control pill" role="group" aria-label="METRIC">
      {#each GEX_METRIC_LABELS as [metric, label]}
        <button type="button" aria-pressed={gridMetric === metric} onclick={() => gridMetric = metric}>
          {label}{#if gridMetric === metric}<span class="grid-swatch up"></span><span class="grid-swatch dn"></span>{/if}
        </button>
      {/each}
    </div>
    {#if grid.ok}
      <span class="pill">{grid.boardPill}</span>
      <span class="pill">{grid.rootPill}</span>
      {#if grid.freshness === "CLOSED" && gridBody?.ok}
        <span class="pill closed" role="status" title={`${gridBody.venue_state?.venue ?? "VENUE"} CLOSED · board ${gridBody.board_date} = last session · next open ${gridBody.venue_state?.next_open ?? "UNSERVED"}`}>CLOSED {grid.ageS === null ? "UNKNOWN" : formatAge(grid.ageS)}</span>
      {:else}
        <span class="pill" class:wrong={grid.stale} role="status" title="Oldest OK column checked time; never storage age">{grid.stale ? "STALE" : "CHECKED"} {grid.ageS === null ? "UNKNOWN" : formatAge(grid.ageS)}</span>
      {/if}
    {/if}
    <span class="chip hyp">HYPOTHESIS</span>
    <AgePill payload={gridAgeBody} fetchedAtMs={gridFetchedAtMs} door="/api/uw/strike-expiry?root=SPX&source=typed" />
    <AgePill payload={sumAgeBody} fetchedAtMs={sumFetchedAtMs} door="/api/uw/strike-expiry?root=SPX&source=typed&window=7d" />
    <PanelInfo id={`${instanceId}-gex-grid-info`} label="GEX GRID information" open={gridInfoOpen} ontoggle={() => gridInfoOpen = !gridInfoOpen}>
      <p>HYPOTHESIS · dealer positioning is the vendor's model, no study passed</p>
      <p>Daily board · checked age is the oldest OK column's check, never stored age.</p>
      <p>Window uses the unscrubbed ladder spot: {windowSpot.kind === "served" ? windowSpot.text : "ABSENT"} · {windowAgeLabel}. The ladder serves snapshot age, not a separate spot-tick age.</p>
      {#if spotError}<p>{spotError}</p>{/if}
      {#if preferenceError}<p>VIEW PREFS ERROR · {preferenceError}</p>{/if}
      <p>{spotProvenanceNote(feed)}</p>
      <p>{gridMetric === "gex" ? "+GEX / −GEX: the sign of the vendor's net exposure; no behaviour is claimed" : gridMetric === "delta" ? "DEX: up is positive · dn is negative vendor delta exposure; no calibrated direction" : `${metricLabel}: up is positive · dn is negative vendor exposure; no calibrated direction`}</p>
      {#if grid.ok}
        <p>One scale across drawn expiries · max |{gridMetric}| {grid.max} · non-zero opacity floor 0.12. Hatch = absent cell or refused column.</p>
        {#each grid.columns as column}<p>{column.expiry} · BOARD {column.boardDate ?? "UNKNOWN"} · {column.state} · checked {column.checkedTs ?? "UNKNOWN"}{column.detail ? ` · ${column.detail}` : ""}</p>{/each}
      {/if}
      {#if sum.ok}
        <p>Σ7D own scale · max |{gridMetric}| {sum.max} · never shade-comparable to one expiry. {sum.complete ? "Complete" : "PARTIAL"} · {sum.expiries.join(", ")}</p>
        <p>Σ7D checked {sum.ageS === null ? "UNKNOWN" : formatAge(sum.ageS)}{sum.freshness !== "CHECKED" ? ` · ${sum.freshness}` : ""} · its own fetch, never the grid's chip</p>
        {#if sum.excluded.length}<p>Σ7D excluded · {sum.excluded.map(e => `${e.expiry} ${e.state}`).join(", ")}</p>{/if}
        {#if sum.gaps.length}<p>Σ7D gaps · {sum.gaps.join(", ")}</p>{/if}
        {#if sum.beyondWindow.length}<p>Σ7D beyond the window · {sum.beyondWindow.join(", ")}</p>{/if}
        <p>Σ7D boards · {sum.boardDates.join(", ") || "NONE"}</p>
      {:else}
        <p>Σ7D · {sum.refusal}</p>
      {/if}
    </PanelInfo>
  </header>
  {#if windowAlert}<span class="chip warn" data-chart-alert role="status">{windowAlert}</span>{/if}
  <div class="grid-measure">
    {#if gridError}<span class="chip warn" data-grid-feed-error role="alert">FEED ERROR</span>{/if}
    {#if sumError}<span class="chip warn" data-sum-feed-error role="alert">FEED ERROR</span>{/if}
    {#if gridRefusal}
      <div class="fault" role="status"><b>{gridRefusal}</b></div>
    {:else if grid.ok}
      <svg class="grid-columns" viewBox="0 0 {W} {HEADER_H}" width={viewportWidth || "100%"} height={HEADER_H * (renderedScale || 1)} preserveAspectRatio="xMinYMin meet" role="img" aria-label="Expiry column headers">
        {#each grid.columns as column, ci}
          {@const x = GUTTER + ci * columnWidth}
            <text class="grid-header" x={x + columnWidth / 2} y="12" text-anchor="middle" font-size={headerFont(column)}>
              <title>{column.header} · BOARD {column.boardDate ?? "UNKNOWN"} · checked {column.checkedTs ?? "UNKNOWN"} · {column.detail ?? column.state}</title>
              {#each headerLines(column) as line, li}<tspan x={x + columnWidth / 2} dy={li === 0 ? 0 : 11}>{line}</tspan>{/each}
            </text>
        {/each}
        <text class="grid-header" x={sumX + columnWidth / 2} y="12" text-anchor="middle" font-size={sumHeaderFont}>
          <title>Σ7D · {sum.ok ? `${sum.header} · ${sum.expiries.join(", ")}` : sum.refusal}</title>
          {#each sumHeaderLines as line, li}<tspan x={sumX + columnWidth / 2} dy={li === 0 ? 0 : 11} class:stale={li === 2 && line === "STALE"}>{line}</tspan>{/each}
        </text>
      </svg>
      <div class="ladderview grid-view" role="region" aria-label="Scrollable strike grid" tabindex="0" bind:this={gridEl} bind:clientWidth={viewportWidth} bind:clientHeight={viewportHeight}
        onwheel={() => manualScroll = true} ontouchmove={() => manualScroll = true} onpointerdown={() => manualScroll = true}
        onkeydown={(e) => { if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"].includes(e.key)) manualScroll = true; }}>
        <svg viewBox="0 0 {W} {gridHeight}" width="100%" height={gridHeight * (renderedScale || 1)} preserveAspectRatio="xMinYMin meet" role="img" aria-label={`${metricLabel} daily board by strike and expiry`} onpointerleave={() => gridReadout = null}>
          <defs><pattern id={hatchId} width="5" height="5" patternUnits="userSpaceOnUse"><path d="M-1 1 L1 -1 M0 5 L5 0 M4 6 L6 4" class="grid-hatch" /></pattern></defs>
          {#each grid.columns as column, ci}
            {@const x = GUTTER + ci * columnWidth}
            {#if column.hatched}
              <rect x={x} y={GRID_TOP} width={columnWidth - 1} height={Math.max(1, gridRows.length) * ROW_H} fill={`url(#${hatchId})`}><title>{column.header} · {column.detail}</title></rect>
            {:else}
              {#each gridRows as strike, ri}
                {@const cell = gridCells.get(`${column.expiry}:${strike}`)!}
                <rect class="grid-cell" x={x} y={GRID_TOP + ri * ROW_H} width={columnWidth - 1} height={ROW_H - 1}
                  fill={cell.absent ? `url(#${hatchId})` : cell.fill === null ? "var(--surface)" : `var(--${cell.fill})`}
                  fill-opacity={cell.absent || cell.fill === null ? 1 : cell.opacity}
                  onpointerenter={() => readGridCell(column, cell)}>
                  <title>{column.expiry} · {strike} · {cell.absent ? "ABSENT" : String(cell.value)} · BOARD {column.boardDate} · {column.state} · checked {column.checkedTs}</title>
                </rect>
              {/each}
            {/if}
          {/each}
          {#if !sum.ok}
            <rect x={sumX} y={GRID_TOP} width={columnWidth - 1} height={Math.max(1, gridRows.length) * ROW_H} fill={`url(#${hatchId})`}><title>Σ7D · {sum.refusal}</title></rect>
          {:else}
            {#each gridRows as strike, ri}
              {@const sumCell = sumCells.get(strike)}
              {#if sumCell === undefined}
                <rect x={sumX} y={GRID_TOP + ri * ROW_H} width={columnWidth - 1} height={ROW_H - 1} fill={`url(#${hatchId})`}><title>Σ7D · {strike} · ABSENT</title></rect>
              {:else}
                <rect class="grid-cell" x={sumX} y={GRID_TOP + ri * ROW_H} width={columnWidth - 1} height={ROW_H - 1}
                  fill={sumCell.fill === null ? "var(--surface)" : `var(--${sumCell.fill})`}
                  fill-opacity={sumCell.fill === null ? 1 : sumCell.opacity}
                  onpointerenter={() => gridReadout = `Σ7D · ${strike} · ${sumCell.value} · ${sumCell.expiriesN} EXP · ${sum.ok && sum.complete ? "COMPLETE" : "PARTIAL"}`}>
                  <title>Σ7D · {strike} · {String(sumCell.value)} · {sumCell.expiriesN} EXP</title>
                </rect>
              {/if}
            {/each}
          {/if}
          {#each gridRows as strike, ri}
            {#if strike % 25 === 0}<text class="grid-strike" x={GUTTER - 8} y={GRID_TOP + ri * ROW_H + 9} text-anchor="end" font-size={labelFontSvg}>{strike}</text>{/if}
          {/each}
          {#if gridSpot.kind !== "absent"}
            <line class="grid-spot" x1={GUTTER} x2={W} y1={gridSpot.y} y2={gridSpot.y} />
            <circle class="grid-spot-dot" cx={GUTTER - 3} cy={gridSpot.y} r="3"><title>Ladder spot {feed?.spot}</title></circle>
          {/if}
        </svg>
      </div>
      {#if gridRows.length === 0}<div class="fault" role="status">NO CELLS IN SPOT WINDOW</div>{/if}
      {#if gridSpot.kind === "absent" && (feed !== null || feedLoaded)}<span class="chip warn" data-chart-alert>SPOT NOT PLACED · {gridSpot.reason}</span>{/if}
      {#if gridSpot.kind === "clamped"}<span class="chip warn" data-chart-alert>SPOT CLAMPED · {gridSpot.edge.toUpperCase()}</span>{/if}
      <output class="grid-readout">{gridReadout ?? ""}</output>
    {/if}
  </div>
</div>

<style>
  .grid-head { flex-wrap: wrap; }
  .grid-control { display: inline-flex; flex-wrap: wrap; gap: 2px; max-width: 100%; }
  .grid-control button { display: inline-flex; align-items: center; gap: 3px; padding: 2px 4px; border: 0; background: transparent; color: var(--muted); font: inherit; cursor: pointer; }
  .grid-control button[aria-pressed="true"] { color: var(--ink); background: var(--surface); }
  .grid-control button:focus-visible { outline: 1px solid var(--ink); }
  .grid-swatch { width: 6px; height: 6px; display: inline-block; }
  .grid-swatch.up { background: var(--up); }
  .grid-swatch.dn { background: var(--dn); }
  .grid-header, .grid-strike { fill: var(--muted); font-family: inherit; }
  .grid-header tspan.stale { fill: var(--stale-ink); }
  .grid-hatch { stroke: var(--muted); stroke-width: 0.7; }
  .grid-cell:hover { stroke: var(--ink); stroke-width: 1; }
  .grid-spot { stroke: var(--ink); stroke-width: 1; pointer-events: none; }
  .grid-spot-dot { fill: var(--ink); }
  .grid-columns { display: block; flex: 0 0 auto; max-width: 100%; }
  .grid-measure { display: flex; flex: 1 1 auto; flex-direction: column; min-height: 0; max-height: 100%; overflow: hidden; }
  .grid-view { flex: 1 1 auto; min-height: 0; max-height: 100%; overflow-x: hidden; overflow-y: auto; }
  .grid-readout { display: block; flex: 0 0 auto; height: 2.6em; line-height: 1.3; overflow-y: auto; font-size: 10px; overflow-wrap: anywhere; color: var(--ink); }

  .grid-body { display: flex; flex-direction: column; gap: 6px; min-width: 0; flex: 1 1 auto; min-height: 0; max-height: 100%; }
  .panel-id { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .pill { display: inline-flex; align-items: center; border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 2px 7px; background: var(--chip); color: var(--muted); font-size: 10px; line-height: 1; }
  .pill.wrong { color: var(--stale-ink); background: var(--stale-soft); border-color: var(--stale); }
  /* D2 (a): StalenessChip's .age.closed face, so a CLOSED board reads as a closure, never as a fault. */
  .pill.closed { color: var(--muted); background: var(--surface-2); border-color: var(--line); font-weight: 600; }
  .warn { color: var(--stale-ink); background: var(--stale-soft); border-color: var(--stale); }
  .railsec { font: 600 11px var(--font-ui); letter-spacing: .08em; color: var(--ink); margin: 0; }
  .ladderview { max-height: min(420px, 100%); overflow-y: auto; overscroll-behavior: contain; }
  .fault { border: 1px dashed var(--stale); background: var(--stale-soft); color: var(--stale-ink); border-radius: var(--radius-chip); padding: 6px 8px; }
  .fault b { font: 600 10px var(--font-ui); letter-spacing: .06em; }
  .chip { font: 10px var(--font-num); color: var(--muted); border: 1px solid var(--line); border-radius: var(--radius-chip); padding: 1px 6px; }
</style>
